import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';
import type { UsageSettingsSaveFailedAction } from '@Application/Ai/AiConfiguration/Assignments/Actions/usageSettingsSaveFailed';
import SaveUsageSettings from '@Application/Ai/AiConfiguration/Assignments/Commands/SaveUsageSettings';
import saveUsageSettings from '@Application/Ai/AiConfiguration/Assignments/saveUsageSettings';
import { theme } from '@Guidelines/Theme/theme';
import usageSettingsSidebarSignal, {
	closeUsageSettingsSidebar,
} from '@UI/Pages/AiAssignments/Shared/Signals/usageSettingsSidebarSignal';
import PromptSettingField from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/Fields/PromptSettingField';
import TemperatureSettingField from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/Fields/TemperatureSettingField';
import VocabularySettingField from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/Fields/VocabularySettingField';
import UsageSettingsFormData from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/FormData/UsageSettingsFormData';
import UsageSettingsResetAction from '@UI/Pages/AiAssignments/Shared/Sidebars/UsageSettingsSidebar/UsageSettingsResetAction';
import type ModelAssignmentsListView from '@UI/Store/AiConfiguration/Views/ModelAssignmentsListView';
import type UsageSettingsView from '@UI/Store/AiConfiguration/Views/UsageSettingsView';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch } from '@verclam/synapse';
import { Button, Form, Sidebar } from '@verclam/ui-kit';

const fieldGroupStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin-bottom: 1.5rem;
`;

const noticeStyle = css`
	margin: 0 0 0.75rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const unavailableBlockStyle = css`
	margin: 0 0 0.5rem;
	padding: 0.625rem 0.875rem;
	border-radius: ${theme.radii.small};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
`;

type Props = {
	readonly scope: AssignmentScope;
	readonly assignments: ModelAssignmentsListView;
};

/**
 * Mounted once by `UsageAssignmentsBoard`, shared by the three affectation screens: `scope` +
 * `assignments` are already loaded there. Its visibility signal
 * (`Shared/Signals/usageSettingsSidebarSignal`) carries the usage value alone; the entry it edits is
 * looked up in `assignments`, never fetched again.
 */
const UsageSettingsSidebar: React.FC<Props> = ({ scope, assignments }) => {
	const dispatch = useDispatch();
	const openedUsage = useSignalValue(usageSettingsSidebarSignal);

	const entry = React.useMemo(
		() =>
			openedUsage !== null
				? (assignments.list.find((item) => item.usage.value === openedUsage) ?? null)
				: null,
		[assignments, openedUsage],
	);

	const [formData, setFormData] = React.useState(() => UsageSettingsFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();

	React.useEffect(() => {
		if (entry !== null) {
			setFormData(UsageSettingsFormData.fromView(entry, scope));
		}
	}, [entry, scope]);

	// Nothing is left to save when every applicable setting is blocked by the affected engine — the
	// "Séparation des locuteurs" scenario (plan §1/§9.3.6). A setting merely inherited (`applicable &&
	// !editable`) still has something to show, so it does not count here.
	const isNothingModifiable = React.useMemo(() => {
		if (entry === null) {
			return false;
		}

		const applicableSettings = [
			entry.settings.vocabularyTerms,
			entry.settings.contextPrompt,
			entry.settings.systemPrompt,
			entry.settings.temperature,
		].filter((setting) => setting.applicable);

		return applicableSettings.every((setting) => setting.editable && !setting.supported);
	}, [entry]);

	const handleVocabularyChange = React.useCallback((value: string) => {
		setFormData((current) => current.setVocabularyTerms(value));
	}, []);

	const handleVocabularyOverriddenChange = React.useCallback((overridden: boolean) => {
		setFormData((current) => current.setIsVocabularyOverridden(overridden));
	}, []);

	const handleContextChange = React.useCallback((value: string) => {
		setFormData((current) => current.setContextPrompt(value));
	}, []);

	const handleContextOverriddenChange = React.useCallback((overridden: boolean) => {
		setFormData((current) => current.setIsContextOverridden(overridden));
	}, []);

	const handleSystemPromptChange = React.useCallback((value: string) => {
		setFormData((current) => current.setSystemPrompt(value));
	}, []);

	const handleSystemPromptOverriddenChange = React.useCallback((overridden: boolean) => {
		setFormData((current) => current.setIsSystemPromptOverridden(overridden));
	}, []);

	const handleTemperatureChange = React.useCallback((value: number | null) => {
		setFormData((current) => current.setTemperature(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (entry === null) {
			return;
		}

		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		setIsSubmitting(true);

		dispatch(saveUsageSettings(new SaveUsageSettings(scope, entry.usage.value, formData.toCommandData())))
			.then(() => {
				closeUsageSettingsSidebar();
			})
			.catch((action: UsageSettingsSaveFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'USAGE_SETTINGS_SAVE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [dispatch, formData, entry, scope]);

	const settingReasons = (settings: UsageSettingsView): Array<{ label: string; reason: string }> => [
		{
			label: 'Vocabulaire métier',
			reason: settings.vocabularyTerms.applicable
				? (settings.vocabularyTerms.unsupportedReason ?? '')
				: 'Ne s’applique pas à cet usage.',
		},
		{
			label: 'Texte de contexte',
			reason: settings.contextPrompt.applicable
				? (settings.contextPrompt.unsupportedReason ?? '')
				: 'Ne s’applique pas à cet usage.',
		},
		{
			label: 'Instruction système',
			reason: settings.systemPrompt.applicable
				? (settings.systemPrompt.unsupportedReason ?? '')
				: 'Ne s’applique pas à cet usage.',
		},
		{
			label: 'Température',
			reason: settings.temperature.applicable
				? (settings.temperature.unsupportedReason ?? '')
				: 'Ne s’applique pas à cet usage.',
		},
	];

	return (
		<Sidebar
			visible={openedUsage !== null}
			onHide={closeUsageSettingsSidebar}
			position="right"
			header={entry !== null ? `Régler l’appel — ${entry.usage.label}` : 'Régler l’appel'}
			footer={
				entry !== null ? (
					<React.Fragment>
						<UsageSettingsResetAction
							scope={scope}
							usage={entry.usage.value}
							usageLabel={entry.usage.label}
							isAccountLevel={formData.includesAccountOnlyFields}
							disabled={isSubmitting}
						/>
						<Button
							type="button"
							severity="secondary"
							outlined
							label="Annuler"
							onClick={closeUsageSettingsSidebar}
						/>
						{isNothingModifiable ? null : (
							<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
						)}
					</React.Fragment>
				) : null
			}
			blockScroll
		>
			{entry === null ? null : isNothingModifiable ? (
				<div css={fieldGroupStyle}>
					<p css={noticeStyle}>
						Aucun réglage d’appel n’est modifiable pour cet usage avec le moteur affecté.
					</p>
					{settingReasons(entry.settings).map(({ label, reason }) => (
						<p key={label} css={unavailableBlockStyle}>
							{label} : {reason}
						</p>
					))}
				</div>
			) : (
				<Form
					id={formId}
					onSubmit={handleSubmit}
					aria-label={`Réglages d’appel — ${entry.usage.label}`}
				>
					<div css={fieldGroupStyle}>
						<VocabularySettingField
							isAccountLevel={formData.includesAccountOnlyFields}
							setting={entry.settings.vocabularyTerms}
							isOverridden={formData.isVocabularyOverridden}
							value={formData.vocabularyTerms}
							onOverriddenChange={handleVocabularyOverriddenChange}
							onValueChange={handleVocabularyChange}
							errorMessage={formData.getValidationMessage('vocabularyTerms')}
							isFormFieldValid={formData.isValid('vocabularyTerms')}
							isSubmitting={isSubmitting}
						/>
					</div>
					<div css={fieldGroupStyle}>
						<PromptSettingField
							name="contextPrompt"
							label="Texte de contexte"
							helpText="Contexte transmis au moteur pour cet usage."
							isAccountLevel={formData.includesAccountOnlyFields}
							setting={entry.settings.contextPrompt}
							isOverridden={formData.isContextOverridden}
							value={formData.contextPrompt}
							onOverriddenChange={handleContextOverriddenChange}
							onValueChange={handleContextChange}
							errorMessage={formData.getValidationMessage('contextPrompt')}
							isFormFieldValid={formData.isValid('contextPrompt')}
							isSubmitting={isSubmitting}
						/>
					</div>
					<div css={fieldGroupStyle}>
						<PromptSettingField
							name="systemPrompt"
							label="Instruction système"
							helpText="Instruction système transmise au moteur pour cet usage."
							isAccountLevel={formData.includesAccountOnlyFields}
							setting={entry.settings.systemPrompt}
							isOverridden={formData.isSystemPromptOverridden}
							value={formData.systemPrompt}
							onOverriddenChange={handleSystemPromptOverriddenChange}
							onValueChange={handleSystemPromptChange}
							errorMessage={formData.getValidationMessage('systemPrompt')}
							isFormFieldValid={formData.isValid('systemPrompt')}
							isSubmitting={isSubmitting}
						/>
					</div>
					<div css={fieldGroupStyle}>
						<TemperatureSettingField
							setting={entry.settings.temperature}
							value={formData.temperature}
							onValueChange={handleTemperatureChange}
							errorMessage={formData.getValidationMessage('temperature')}
							isFormFieldValid={formData.isValid('temperature')}
							isSubmitting={isSubmitting}
						/>
					</div>
				</Form>
			)}
		</Sidebar>
	);
};

export default UsageSettingsSidebar;
